import React, { useState, useEffect } from "react";
import { Trash2, Plus, Database, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../api-client/AxiosClient";
import CrateDb from "../../../../assets/crateDb.png";
import { toast } from "react-toastify";
import "./DataSource.css";
import {
  FaFileExcel,
  FaFileCode,
  FaNetworkWired,
  FaServer,
  FaCloud,
  FaCogs,
  FaSalesforce,
} from "react-icons/fa";
import { SiPostgresql, SiMysql } from "react-icons/si";
import sql from "../../../../assets/sql1.png";
import { validateCorporationData } from "../../../../utils/dataValidation";
import IBMDB2logo1 from "../../../../assets/IBMDB2logo1.png";

const DatabaseConnectionsTable = () => {
  const navigate = useNavigate();
  const { DataSource } = JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCorporation, setSelectedCorporation] = useState(null);

  const tableHeadings = [
    "Name",
    "Description",
    "Data Source Type",
    "Created On",
    "Status",
    "Actions",
  ];

  const getDBTypeIcon = (dbType) => {
    const iconProps = "w-6 h-6 mr-2";

    console.log(dbType);
    switch (dbType?.toLowerCase()) {
      case "json":
        return <FaFileCode className={`${iconProps} text-yellow-600`} />;
      case "xml":
        return <FaFileCode className={`${iconProps} text-orange-600`} />;
      case "excel/csv":
      case "csv":
      case "excel":
        return <FaFileExcel className={`${iconProps} text-green-600`} />;
      case "api":
        return <FaNetworkWired className={`${iconProps} text-blue-600`} />;
      case "postgresql":
      case "postgres":
      case "postgressql":
        return <SiPostgresql className={`${iconProps} text-blue-700`} />;
      case "mysql":
      case "MsSql":
      case "mssql":
        return <img src={sql} alt="sql" className="w-8 h-8 mr-2" />;
      case "sql server":
      case "sqlserver":
        return <FaServer className={`${iconProps} text-red-600`} />;
      case "ibm db2":
      case "db2":
        return <img src={IBMDB2logo1} alt="CrateDb" className={iconProps} />;
      case "sap hana":
      case "hana":
        return <FaCloud className={`${iconProps} text-teal-600`} />;
      case "cratedb":
        return <img src={CrateDb} alt="CrateDb" className={iconProps} />;
      case "salesforce":
        return <FaSalesforce className={`${iconProps} text-[#007bff]`} />;
      default:
        return <FaServer className={`${iconProps} text-gray-600`} />;
    }
  };

 useEffect(() => {
  try {
    const storedCorporation = localStorage.getItem("selectedCorporation");
    if (storedCorporation) {
      const parsedCorporation = JSON.parse(storedCorporation);
      // Validate before using
      const validatedCorp = validateCorporationData(parsedCorporation);
      setSelectedCorporation(validatedCorp);
    }
  } catch (error) {
    console.error("Invalid corporation data:", error);
    setError("Failed to load corporation data");
    localStorage.removeItem("selectedCorporation");
  }
}, []);

  // 3. Add listener for localStorage changes
 useEffect(() => {
  const handleStorageChange = (e) => {
    if (e.key === "selectedCorporation") {
      try {
        const newCorporation = e.newValue 
          ? validateCorporationData(JSON.parse(e.newValue))
          : null;
        setSelectedCorporation(newCorporation);

        if (newCorporation) {
          fetchConnections(newCorporation.value);
        } else {
          setConnections([]);
        }
      } catch (error) {
        console.error("Invalid corporation data from storage:", error);
        localStorage.removeItem("selectedCorporation");
        setError("Corporation data corrupted");
      }
    }
  };

  const handleCustomStorageChange = (e) => {
    if (e.detail?.key === "selectedCorporation") {
      try {
        const newCorporation = e.detail.newValue
          ? validateCorporationData(JSON.parse(e.detail.newValue))
          : null;
        setSelectedCorporation(newCorporation);

        if (newCorporation) {
          fetchConnections(newCorporation.value);
        } else {
          setConnections([]);
        }
      } catch (error) {
        console.error("Invalid corporation data from custom event:", error);
        setError("Corporation data corrupted");
      }
    }
  };

  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("customStorageChange", handleCustomStorageChange);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("customStorageChange", handleCustomStorageChange);
  };
}, []);

  // 4. Add useEffect to fetch connections when selectedCorporation changes (add after the above useEffect)
  useEffect(() => {
    if (selectedCorporation) {
      fetchConnections(selectedCorporation.value);
    }
  }, [selectedCorporation]);

  // Add state for confirmation dialog
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    connectionId: null,
    connectionName: "",
  });

  // Fetch database connections based on corporation ID
  const fetchConnections = async (corporationId) => {
    if (!corporationId) {
      setConnections([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosClient.get(
        `/api/MappingDB/CorpMappingDBByUserCorp?corporationID=${corporationId}`
      );

      if (response && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        setConnections(data);
      } else {
        setConnections([]);
        toast.error("Failed to load database connections");
      }
    } catch (err) {
      setError(
        "Failed to fetch database connections: " +
          (err.message || "Unknown error")
      );
      setConnections([]);
      toast.error(
        "Error loading database connections: " +
          (err.message || "Unknown error")
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  // Updated handleDelete function with custom confirmation
  const handleDelete = async (connectionId, connectionName = "") => {
    // Show confirmation dialog instead of window.confirm
    setDeleteConfirm({
      show: true,
      connectionId,
      connectionName,
    });
  };

  const confirmDelete = async () => {
    const { connectionId } = deleteConfirm;

    try {
      // Use POST method with proper request body structure
      const response = await axiosClient.post(
        "/api/MappingDB/DeleteDataSource",
        {
          corpMappingDBIds: [connectionId], // Send as array as expected by API
        }
      );

      // Check if deletion was successful
      if (response && response.data) {
        const { deletedIds, failedDueToDependency } = response.data;

        if (deletedIds && deletedIds.includes(connectionId)) {
          setConnections((prev) =>
            prev.filter((conn) => conn.corpmappingdbid !== connectionId)
          );

          toast.success("Data source deleted successfully");
        } else if (
          failedDueToDependency &&
          failedDueToDependency.includes(connectionId)
        ) {
          // Handle case where deletion failed due to dependencies
          toast.error(
            "Unable to delete data source because it is associated with a mapping file."
          );
        } else {
          toast.error("Failed to delete data source");
        }
      } else {
        toast.error("Something went wrong");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setDeleteConfirm({ show: false, connectionId: null, connectionName: "" });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, connectionId: null, connectionName: "" });
  };

  // Custom Confirmation Dialog Component
  const ConfirmationDialog = () => {
    if (!deleteConfirm.show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Delete Connection
              </h3>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this database connection?
              {deleteConfirm.connectionName && (
                <span className="font-medium text-gray-700">
                  {" "}
                  "{deleteConfirm.connectionName}"
                </span>
              )}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This action cannot be undone.
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={cancelDelete}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              No, Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleAddDataSource = () => {
    navigate("/data-sources/add-data-source", {
      state: { selectedCorporation },
    });
  };

  const getStatusBadge = (isActive) => {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
            isActive ? "bg-green-400" : "bg-red-400"
          }`}
        ></span>
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Connections
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() =>
                selectedCorporation &&
                fetchConnections(selectedCorporation.value)
              }
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show corporation selector first if no corporation is selected
  if (!selectedCorporation) {
    return (
      <div className="flex items-center justify-center loader-height">
        <div className="p-6">
          <div className="text-center py-12">
            {/* Corporation dropdown */}
            {/* <div className="mb-6 flex justify-center">
              <CorporationSelector
                onSelect={handleCorporationSelect}
                selectedValue={selectedCorporation}
                autoSelectFirst={true}
                sortAlphabetically={true}
              />
            </div> */}
          </div>
        </div>
      </div>
    );
  }

  // Empty state - show only Add Data Sources button
  if (connections.length === 0 && !loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Database Connections
          </h3>
          {DataSource?.canAdd && (
            <>
              <p className="text-gray-600 mb-6">
                Get started by adding your first database connection for{" "}
                <b>{selectedCorporation.label}.</b>
              </p>

              <button
                onClick={handleAddDataSource}
                className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Data Source
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Table view with data
  return (
    <>
      {connections.length !== 0 ? (
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Registered Data Sources
              </h2>
              <p className="text-gray-600">
                Manage database connections for{" "}
                <span className="font-medium text-gray-900">
                  {selectedCorporation?.label || "Selected Corporation"}
                </span>
              </p>
            </div>

            {DataSource?.canAdd && (
              <div className="flex flex-col items-end space-y-4">
                <button
                  onClick={handleAddDataSource}
                  className="inline-flex items-center text-white px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Data Source
                </button>
              </div>
            )}
          </div>

          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {tableHeadings.map((heading) => {
                      const isCenterAligned = ["Status", "Actions"].includes(
                        heading
                      );

                      if (!DataSource.canDelete && heading === "Actions") {
                        return null;
                      }
                      return (
                        <th
                          key={heading}
                          className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            isCenterAligned ? "text-center" : "text-left"
                          }`}
                        >
                          {heading}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {loading ? (
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td colSpan="100%">
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-3 text-gray-600">
                            Loading database connections...
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody className="bg-white divide-y divide-gray-200">
                    {connections.map((connection) => (
                      <tr
                        key={connection.corpmappingdbid}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Database className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {connection.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm text-gray-900 max-w-xs truncate"
                            title={connection.description}
                          >
                            {connection.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-gray-900">
                          <div className="flex items-center justify-start">
                            {getDBTypeIcon(connection.mappingdbtype)}
                            <span className="ml-2">
                              {connection.mappingdbtype}
                            </span>
                          </div>
                        </td>
                        <td classname="px-6 py-4 text-sm text-gray-500 text-center whitespace-nowrap">
                          {connection.created_at
                            ? new Date(connection.created_at).toLocaleString()
                            : "_"}
                        </td>

                        <td className="px-6 py-4 text-center align-middle">
                          {getStatusBadge(connection.isactive)}
                        </td>
                        {DataSource.canDelete && (
                          <td className="px-6 py-4 text-center align-middle">
                            <button
                              onClick={() =>
                                handleDelete(
                                  connection.corpmappingdbid,
                                  connection.name
                                )
                              }
                              className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100 transition-colors"
                              title="Delete connection"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>

          <div className="my-4 text-sm text-gray-500">
            Showing {connections.length} database connection
            {connections.length !== 1 ? "s" : ""} for{" "}
            <span className="font-medium">
              {selectedCorporation?.label || "Selected Corporation"}
            </span>
          </div>

          {/* Custom Confirmation Dialog */}
          <ConfirmationDialog />
        </div>
      ) : (
        <div className="flex items-center justify-center loader-height">
          <div className="p-6">
            <div className="text-center py-12">
              <div className="mb-6 flex justify-center">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DatabaseConnectionsTable;
